import { computed, observable, makeObservable, action, reaction } from "mobx";
import { composeKey } from "../../helpers";
import { computedFn } from "mobx-utils";
import { compareBySortOrder, compareMarkets, parseWidgetSelection, } from "../helpers";
import { BooleanIndex, PLAYER_PAGE_ERROR } from "../../../../businessLayer";
import { SELECTION_TOGGLE_FALLBACK_TIMEOUT } from "blocks/Marketboard/constants";
import { disposer, pointsFormatBuilder } from "@draftkings/sportsbook-common-utils";
import { marketCellsBuilder, templateTypeMapper } from "@draftkings/component-builder";
import { marketBoardDict } from "blocks/Marketboard/dict";
const isLiveEvent = (status) => status === "STARTED";
export class MarketboardParser {
    constructor(options) {
        this.getMarketsGrid = computedFn((options) => {
            var _a, _b;
            return marketCellsBuilder({
                eventId: options.eventId,
                loadingSelectionIndex: this.loadingSelectionIndex,
                selectedSelectionIndex: this.selectedSelectionIndex,
                isTeamSwap: ((_a = this.retriever.leagues.get(options.leagueId)) === null || _a === void 0 ? void 0 : _a.isTeamSwap) || false,
                localization: this.localization,
                componentId: options.componentId,
                oddsStyle: this.oddsStyle,
                categoryId: options.sectionName,
                subCategoryId: options.id,
                onSelectionClick: (ids) => this.toggleSelections([ids]),
                markets: this.parseMarkets({ eventId: options.eventId, subCategoryId: options.id }),
                participants: [],
                logError: this.trackEvent.bind(this, PLAYER_PAGE_ERROR),
                featureFlags: this.featureFlags,
                productConfig: this.productConfig,
                consumerPageName: "PlayerPage",
                staticS3Host: this.staticS3Host,
                logos: this.logos,
                eventStatus: options.eventStatus,
                dict: Object.assign(Object.assign({}, marketBoardDict), (((_b = this.localizationConfig) === null || _b === void 0 ? void 0 : _b.playerPage) || {})),
                localizationConfig: this.localizationConfig
            });
        });
        this.mapSubCategoriesToSection = (sectionName) => {
            const markets = [...this.retriever.markets.values()].filter(m => m.size > 0);
            const section = this.retriever.sections.get(sectionName);
            const subCategoriesIds = [];
            if (section.filter) {
                const matchTags = section.filter.markets.matchTags;
                const sectionMarkets = section.filter.markets;
                markets.forEach(m => {
                    const market = [...m.values()][0];
                    if ((matchTags && sectionMarkets.tags.every(tag => { var _a; return (_a = market.tags) === null || _a === void 0 ? void 0 : _a.includes(tag); })) ||
                        (!matchTags && !sectionMarkets.tags.some(tag => { var _a; return (_a = market.tags) === null || _a === void 0 ? void 0 : _a.includes(tag); }))) {
                        subCategoriesIds.push(market.subcategoryId);
                    }
                });
            }
            return subCategoriesIds;
        };
        this.parseSections = ({ eventId, leagueId, sportId, subCategories }) => [...this.retriever.sections.values()]
            .sort(compareBySortOrder)
            .map(section => {
            const subCategoriesIds = this.mapSubCategoriesToSection(section.name);
            const subCategoriesPerSection = subCategories.filter(sc => subCategoriesIds.includes(sc.id));
            return {
                name: section.name,
                isExpanded: this.isExpandedSection({
                    name: section.name,
                    eventId
                }),
                subCategories: this.parseSubCategories({
                    subCategories: subCategoriesPerSection,
                    isH2H: section.name.includes("H2H"),
                    eventId,
                    leagueId,
                    sportId
                }),
                tabFormat: section.tabFormat,
                onClick: () => this.toggleSection(eventId, section.name),
            };
        })
            .filter(s => s.subCategories.length > 0);
        this.parseSubCategories = ({ subCategories, isH2H, eventId, leagueId, sportId }) => {
            const selectedSubCategory = this.selectedSubCategoryByEventId.get(eventId);
            return subCategories
                .sort(compareBySortOrder)
                .map((sc, i) => {
                var _a;
                return (Object.assign(Object.assign({}, sc), { isExpanded: this.isExpanded({
                        isExpandedByDefault: isH2H ? true : !!Math.max(0, this.expandedSubCategoriesByDefault--),
                        eventId,
                        subCategoryId: sc.id,
                    }), isSelected: selectedSubCategory ? selectedSubCategory.id === sc.id : i === 0, isH2H, onClick: () => this.toggleSubCategory({
                        eventId,
                        leagueId,
                        sportId,
                        subCategory: sc,
                        isH2H
                    }), showSgpBadge: this.isSgpTagsOnMarketLevelEnabled && ((_a = sc.tags) === null || _a === void 0 ? void 0 : _a.includes("SGP")) }));
            });
        };
        this.parseMarkets = (options) => {
            var _a;
            const key = composeKey([options.eventId, options.subCategoryId]);
            const markets = (_a = this.retriever
                .markets
                .get(key)) === null || _a === void 0 ? void 0 : _a.values();
            if (markets) {
                return [...markets]
                    .reduce((acc, m) => {
                    const selections = this.retriever.selections.get(m.id);
                    if (selections) {
                        acc.push(Object.assign(Object.assign({}, m), { selections, statistics: m.statistics }));
                    }
                    return acc;
                }, [])
                    .sort(compareMarkets);
            }
            return [];
        };
        this.toggleSubCategory = ({ eventId, leagueId, sportId, subCategory, isH2H }) => {
            const defaultMetadata = {
                action: "click",
                sport: sportId,
                league: leagueId,
                subcategoryId: subCategory.id,
                value: subCategory.name,
            };
            this.isInitialSubCategoryLoad && (this.isInitialSubCategoryLoad = false);
            if (isH2H) {
                this.selectedSubCategoryByEventId.set(eventId, subCategory);
                this.toggleSubCategoryAnalytics(Object.assign({ type: "ChangeSubcategory", section: "GamesCategory" }, defaultMetadata));
                return;
            }
            const expandedSubCategoryKey = composeKey([eventId, subCategory.id]);
            if (this.expandedSubCategories.has(expandedSubCategoryKey)) {
                this.expandedSubCategories.delete(expandedSubCategoryKey);
                this.toggleSubCategoryAnalytics(Object.assign({ type: "CollapseEvent", section: "GamesComponent" }, defaultMetadata));
            }
            else {
                this.expandedSubCategories.add(expandedSubCategoryKey);
                this.toggleSubCategoryAnalytics(Object.assign({ type: "ExpandEvent", section: "GamesComponent" }, defaultMetadata));
            }
        };
        this.toggleSection = (eventId, sectionName) => {
            this.isInitialSectionLoad && (this.isInitialSectionLoad = false);
            const expandedSectionKey = composeKey([eventId, sectionName]);
            if (this.expandedSections.has(expandedSectionKey)) {
                this.expandedSections.delete(expandedSectionKey);
            }
            else {
                this.expandedSections.add(expandedSectionKey);
            }
        };
        this.replaceSelectedSelections = (selections) => {
            selections.forEach(s => {
                if (s.replacedSelectionId) {
                    const isPrevSelectionAdded = this.betslipSelections.get(s.replacedSelectionId) === "Added";
                    this.betslipSelections.delete(s.replacedSelectionId);
                    if (isPrevSelectionAdded) {
                        this.betslipSelections.set(s.id, "Added");
                    }
                }
            });
        };
        this.oddsStyle = options.oddsStyle;
        this.localizationLib = options.localizationLib;
        this.retriever = options.retriever;
        this.expandedSubCategoriesByDefault = options.expandedSubCategoriesByDefault;
        this.expandedSubCategories = new Set();
        this.expandedSections = new Set();
        this.selectedSubCategoryByEventId = new Map();
        this.betslipSelections = this.parseBetslipSelection(options.betslipSelections);
        this.consumerCommands = options.consumerCommands;
        this.showOverlay = false;
        this.isInitialSubCategoryLoad = true;
        this.isInitialSectionLoad = true;
        this.featureFlags = options.featureFlags;
        this.productConfig = options.productConfig;
        this.isSgpTagsOnMarketLevelEnabled = options.featureFlags["RLS-OSB-WEB-SGPTagsOnMarketLevelEnabled"];
        this.toggledSelectionsFallbackHandler = new Map();
        this.logos = options.logos;
        this.staticS3Host = options.s3AssetHost;
        this.replacedSelectionDisposer = disposer;
        this.isMobile = options.isMobile;
        this.trackEvent = options.trackEvent;
        this.onMobileToggleSelection = options.onMobileToggleSelection;
        this.localizationConfig = options.localizationConfig;
        this.casesPerStatus = {
            Adding: id => {
                this.betslipSelections.set(id, "Added");
                this.getOutcomeAnalytics({ status: "Added", selectionId: id });
            },
            Added: id => {
                this.betslipSelections.delete(id);
                this.getOutcomeAnalytics({ status: "Removed", selectionId: id });
            },
        };
        this.selectedSelectionIndex = new BooleanIndex((selectionId) => {
            const status = this.betslipSelections.get(selectionId);
            return status === "Added" && status;
        }, () => true);
        this.loadingSelectionIndex = new BooleanIndex((selectionId) => {
            const status = this.betslipSelections.get(selectionId);
            return status !== "Added" && status;
        }, () => true);
        makeObservable(this, {
            playerId: computed,
            events: computed,
            showOverlay: observable,
            oddsStyle: observable,
            selectedSubCategoryByEventId: observable,
            betslipSelections: observable,
            expandedSubCategories: observable,
            expandedSections: observable,
            addSelections: action,
            removeSelections: action,
            toggleSelections: action.bound,
            toggleSubCategory: action.bound,
            toggleSection: action.bound,
            handleSelectionToggleFallback: action,
            clearData: action,
            replaceSelectedSelections: action,
        });
    }
    activate() {
        this.retriever.activate();
        this.replacedSelectionDisposer = reaction(() => [...this.retriever.selections.values()]
            .flatMap(selectionsMap => [...selectionsMap.values()])
            .filter((s) => !!s.replacedSelectionId
            && this.betslipSelections.has(s.replacedSelectionId)), this.replaceSelectedSelections);
    }
    deactivate() {
        this.retriever.deactivate();
        this.clearData();
        this.replacedSelectionDisposer();
    }
    getPrePacks() {
        throw new Error("Method not implemented.");
    }
    setOddsStyle(oddsStyle) {
        this.oddsStyle = oddsStyle;
    }
    setParams(params) {
        this.retriever.setParams(params);
    }
    addSelections(data) {
        data.selections.forEach(selection => {
            this.showOverlay = false;
            if (selection.isSuccessful) {
                this.casesPerStatus["Adding"](selection.id);
            }
            else {
                this.casesPerStatus["Added"](selection.id);
            }
            this.cancelSelectionToggleFallback(selection.id);
        });
    }
    removeSelections(data) {
        data.selections.forEach(selection => {
            if (selection.isRemovable) {
                this.casesPerStatus["Added"](selection.id);
            }
            else {
                this.casesPerStatus["Adding"](selection.id);
            }
            this.showOverlay = false;
            this.cancelSelectionToggleFallback(selection.id);
        });
    }
    toggleSelections(selectionIds) {
        if (this.isMobile()) {
            this.onMobileToggleSelection();
            return;
        }
        selectionIds.forEach(ids => {
            var _a, _b;
            const status = this.betslipSelections.get(ids.selectionId);
            const event = this.retriever.events.get(ids.eventId);
            const market = (_a = this.retriever.markets.get(composeKey([ids.eventId, ids.subCategoryId]))) === null || _a === void 0 ? void 0 : _a.get(ids.marketId);
            const selection = (_b = this.retriever.selections.get(ids.marketId)) === null || _b === void 0 ? void 0 : _b.get(ids.selectionId);
            if (event && market && selection) {
                const parseSelection = parseWidgetSelection(Object.assign(Object.assign({}, selection), { marketName: market.name, eventId: ids.eventId, eventName: event.name, prePackId: undefined }));
                const isLive = isLiveEvent(event.status);
                if (status) {
                    this.betslipSelections.set(ids.selectionId, "Removing");
                    this.handleSyncRemoveSelection(parseSelection, isLive);
                }
                else {
                    this.betslipSelections.set(ids.selectionId, "Adding");
                    this.handleSyncAddSelection(parseSelection, isLive);
                }
                this.showOverlay = true;
                this.handleSelectionToggleFallback(ids.selectionId, ids.eventId, status);
            }
        });
    }
    handleSelectionToggleFallback(selectionId, eventId, status) {
        const timeoutId = setTimeout(() => {
            if (status) {
                this.betslipSelections.set(selectionId, status);
            }
            else {
                this.betslipSelections.delete(selectionId);
            }
            this.toggledSelectionsFallbackHandler.delete(selectionId);
            this.showOverlay = false;
            this.trackEvent("SELECTION_TOGGLE_TIMED_OUT", {
                eventId,
                selectionId,
                pageName: "PLAYER_PAGE",
                widget: "PLAYER_PAGE_WIDGET",
            });
        }, SELECTION_TOGGLE_FALLBACK_TIMEOUT);
        this.toggledSelectionsFallbackHandler.set(selectionId, () => clearTimeout(timeoutId));
    }
    cancelSelectionToggleFallback(selectionId) {
        const cancelToggleSelectionFallback = this.toggledSelectionsFallbackHandler.get(selectionId);
        cancelToggleSelectionFallback === null || cancelToggleSelectionFallback === void 0 ? void 0 : cancelToggleSelectionFallback();
        this.toggledSelectionsFallbackHandler.delete(selectionId);
    }
    loadData(playerId) {
        this.retriever.loadData(playerId);
    }
    get events() {
        return [...this.retriever.events.values()].map(e => {
            var _a, _b, _c;
            const subCategories = ((_a = e.subcategories) === null || _a === void 0 ? void 0 : _a.filter(x => templateTypeMapper[x.componentId])) || [];
            return {
                id: e.id,
                status: e.status,
                leagueId: e.leagueId,
                sections: this.parseSections({ eventId: e.id, leagueId: e.leagueId, sportId: e.sportId, subCategories }),
                leagueName: ((_b = this.retriever.leagues.get(e.leagueId)) === null || _b === void 0 ? void 0 : _b.name) || "",
                sportName: ((_c = this.retriever.sports.get(e.sportId)) === null || _c === void 0 ? void 0 : _c.name) || "",
            };
        });
    }
    get playerId() {
        return this.retriever.playerId;
    }
    isExpandedSection({ name, eventId }) {
        const expandedSectionKey = composeKey([eventId, name]);
        const hasExpandedSection = this.expandedSections.has(expandedSectionKey);
        if (this.isInitialSectionLoad && !hasExpandedSection) {
            this.expandedSections.add(expandedSectionKey);
            return true;
        }
        return hasExpandedSection;
    }
    isExpanded({ isExpandedByDefault, eventId, subCategoryId }) {
        const expandedSubCategoryKey = composeKey([eventId, subCategoryId]);
        const hasExpandedSubCategories = this.expandedSubCategories.has(expandedSubCategoryKey);
        if (isExpandedByDefault && this.isInitialSubCategoryLoad && !hasExpandedSubCategories) {
            this.expandedSubCategories.add(expandedSubCategoryKey);
            return true;
        }
        return hasExpandedSubCategories;
    }
    handleSyncAddSelection(parseSelection, isLive) {
        this.sendOutcomeAnalytics({ status: "IsAdding", selectionId: parseSelection.selectionId });
        this.consumerCommands.syncAddSelections({
            selections: [parseSelection],
            isLive
        });
    }
    handleSyncRemoveSelection(parseSelection, isLive) {
        this.sendOutcomeAnalytics({ status: "Removed", selectionId: parseSelection.selectionId });
        this.consumerCommands.syncRemoveSelections({
            selections: [parseSelection],
            isLive
        });
    }
    parseBetslipSelection(betslipSelectionsData) {
        // Todo the logic related to prepack id will part of prepack integration
        // https://jira.dkcorpit.com/browse/SB-27998
        return new Map(betslipSelectionsData.regularSelections.map(s => [s.id, "Added"]));
    }
    clearData() {
        this.expandedSubCategories.clear();
        this.selectedSubCategoryByEventId.clear();
        this.betslipSelections.clear();
        this.isInitialSubCategoryLoad = true;
    }
    get localization() {
        return { formatPoints: pointsFormatBuilder(this.localizationLib) };
    }
    sendOutcomeAnalytics({ status, selectionId }) {
        this.consumerCommands.sendAnalytics({
            playerId: this.retriever.playerId,
            type: "OutcomeAction",
            metadata: {
                status,
                selectionId
            },
        });
    }
    getOutcomeAnalytics({ status, selectionId, errorStatusCode }) {
        this.consumerCommands.sendAnalytics({
            playerId: this.retriever.playerId,
            type: "OutcomeResult",
            metadata: {
                status,
                selectionId,
                errorStatusCode,
            },
        });
    }
    toggleSubCategoryAnalytics({ type, sport, league, subcategoryId, value, section }) {
        this.consumerCommands.sendAnalytics({
            playerId: this.retriever.playerId,
            type,
            metadata: {
                action: "click",
                sport,
                league,
                subcategoryId,
                value,
                section,
            },
        });
    }
}
//# sourceMappingURL=MarketboardParser.js.map